import { gql } from '@apollo/client';
import { TRANSLATED_STRING_FIELDS } from '@assets/i18n/config';
import {
  type FinancialProduct,
  type Goal,
  type SubAccount,
  type Theme,
  ThemeState,
} from '@generated/graphql';
import { householdOwnsPortfolio } from '@ovComponents/4-module/householdPortfolio/householdOwnsPortfolio';
import { PageObjectType } from '@pages/setting/PageConfigurationDetail/pageConfigurationTypes';
import { min } from 'lodash-es';

// Unified data structure for subaccount portfolio data
export type SubAccountPortfolioData = {
  id: string;
  name: string;
  themeId: string | null;
  availableThemes: Theme[];
  suitabilityScore: Goal['suitabilityScore'];
  custodianSuitabilityScore: number;
  userSuitability: number | null;
  suggestedFinancialProduct: FinancialProduct | null;
  financialProduct: FinancialProduct | null;
  availableFinancialProducts: FinancialProduct[];
  modelPortfoliosThemeId: string | null;
  modelPortfolioLoadFailed: boolean;
  modelPortfolioLoadFailureVersion: number;
  suggestionMissed: boolean;
  isFromGoal?: boolean; // Indicates if this is goal-level data
};

// Configuration for portfolio data transformation
export type PortfolioDataTransformConfig = {
  manuallyAssignModelPortfolios: boolean;
  userSuitability: number | null;
  custodianSuitabilityScore: number;
  currentAccountId: string;
  allThemesData?: {
    fetchThemes?: {
      themes: Theme[];
    };
  };
  isCreateSubAccountForAccountHardcodedWorkflow?: boolean;
};

export const AVAILABLE_THEMES = `#graphql
  availableThemes {
    id
    key
    translatedName { ${TRANSLATED_STRING_FIELDS} }
    translatedDescription { ${TRANSLATED_STRING_FIELDS} }
    core
  }
`;

const LINKED_HOUSEHOLD_PORTFOLIO = `#graphql
  householdClientGroup {
    id
    financialProduct {
      id
    }
  }
`;

type LinkedHousehold = {
  householdClientGroup?: {
    id?: string;
    financialProduct?: { id?: string | null } | null;
  } | null;
};

export type AssignPortfolioHouseholdOwnershipInput = {
  householdClientGroupFinancialProductId?: string | null;
  fetchedSubAccount?: {
    goal?: LinkedHousehold | null;
    account?: LinkedHousehold | null;
  } | null;
  fetchedGoal?:
    | (LinkedHousehold & {
        subAccounts?: Array<{
          goal?: LinkedHousehold | null;
          account?: LinkedHousehold | null;
        } | null> | null;
      })
    | null;
  enableMultiSubAccountMode: boolean;
};

const linkedOwns = (entity?: LinkedHousehold | null) =>
  householdOwnsPortfolio(entity?.householdClientGroup?.financialProduct?.id);

export const resolveAssignPortfolioHouseholdOwnership = ({
  householdClientGroupFinancialProductId,
  fetchedSubAccount,
  fetchedGoal,
  enableMultiSubAccountMode,
}: AssignPortfolioHouseholdOwnershipInput): boolean => {
  if (householdOwnsPortfolio(householdClientGroupFinancialProductId)) {
    return true;
  }

  if (
    linkedOwns(fetchedSubAccount?.goal) ||
    linkedOwns(fetchedSubAccount?.account)
  ) {
    return true;
  }

  if (linkedOwns(fetchedGoal)) {
    return true;
  }

  if (enableMultiSubAccountMode && fetchedGoal?.subAccounts?.length) {
    return fetchedGoal.subAccounts.some(
      (subAccount) =>
        linkedOwns(subAccount?.goal) || linkedOwns(subAccount?.account),
    );
  }

  return false;
};

export const SUGGESTED_PRODUCT = `#graphql
  id
  translatedName { ${TRANSLATED_STRING_FIELDS} }
  translatedUrl { ${TRANSLATED_STRING_FIELDS} }
  children {
    id
    percentage
    financialProduct {
      id
      translatedName { ${TRANSLATED_STRING_FIELDS} }
      primaryAssetClass {
        id
        translatedName { ${TRANSLATED_STRING_FIELDS} }
        translatedDescription { ${TRANSLATED_STRING_FIELDS} }
        key
      }
      secondaryAssetClass {
        id
        translatedName { ${TRANSLATED_STRING_FIELDS} }
        translatedDescription { ${TRANSLATED_STRING_FIELDS} }
        key
      }
    }
  }
  theme {
    id
  }
`;

export const UPDATE_SUB_ACCOUNT = gql`
  mutation updateSubAccount($input: UpdateSubAccountInput!) {
    updateSubAccount(input: $input) {
      subAccount {
        id
        suggestedFinancialProduct {
          ${SUGGESTED_PRODUCT}
        }
      }
    }
  }
`;

export const UPDATE_GOAL = gql`
  mutation updateGoal($input: UpdateGoalInput!) {
    updateGoal(input: $input) {
      goal {
        id
        suitabilityScore
        suggestedFinancialProduct {
          ${SUGGESTED_PRODUCT}
        }
      }
    }
  }
`;

export const FETCH_SUB_ACCOUNT = gql`
  query fetchSubAccount($subAccountId: ObjectID!) {
    fetchSubAccount(subAccountId: $subAccountId) {
      subAccount {
        id
        suitabilityScore
        ${AVAILABLE_THEMES}
        goal {
          ${LINKED_HOUSEHOLD_PORTFOLIO}
        }
        account {
          ${LINKED_HOUSEHOLD_PORTFOLIO}
        }
        suggestedFinancialProduct {
          ${SUGGESTED_PRODUCT}
        }
        financialProduct {
          id
          theme {
            id
          }
        }
      }
    }
  }
`;

export const FETCH_GOAL = gql`
  query fetchGoal($goalId: ObjectID!) {
    fetchGoal(goalId: $goalId) {
      goal {
        id
        suitabilityScore
        ${AVAILABLE_THEMES}
        householdClientGroup {
          id
          financialProduct {
            id
          }
        }
        suggestedFinancialProduct {
          ${SUGGESTED_PRODUCT}
        }
        financialProduct {
          id
          theme {
            id
          }
        }
      }
    }
  }
`;

export const FETCH_GOAL_WITH_SUBACCOUNTS = gql`
  query fetchGoalWithSubAccounts($goalId: ObjectID!) {
    fetchGoal(goalId: $goalId) {
      goal {
        id
        suitabilityScore
        ${AVAILABLE_THEMES}
        householdClientGroup {
          id
          financialProduct {
            id
          }
        }
        suggestedFinancialProduct {
          ${SUGGESTED_PRODUCT}
        }
        financialProduct {
          id
          theme {
            id
          }
        }
        subAccounts {
          id
          name
          state
          suitabilityScore
          ${AVAILABLE_THEMES}
          account {
            id
            ${LINKED_HOUSEHOLD_PORTFOLIO}
          }
          suggestedFinancialProduct {
            ${SUGGESTED_PRODUCT}
          }
          financialProduct {
            id
            theme {
              id
            }
          }
        }
      }
    }
  }
`;

export const FETCH_USER_SUITABILITY = gql`
  query fetchUserSuitability($userId: SafeString!) {
    fetchUser(input: { userId: $userId }) {
      user {
        id: stringId
        suitabilityScore
      }
    }
  }
`;

export const FETCH_HOUSEHOLD_SUITABILITY = gql`
  query fetchHouseholdSuitability($clientGroupId: SafeString!) {
    fetchClientGroup(input: { clientGroupId: $clientGroupId }) {
      clientGroup {
        financialProduct {
          id
        }
        primaryUser {
          id: stringId
          suitabilityScore
        }
      }
    }
  }
`;

export const SUGGEST_PORTFOLIO = gql`
  query suggestPortfolio($suitabilityScore: Int!, $organizationId: ObjectID!, $themeId: ObjectID) {
    suggestFinancialProduct(input: {
      suitabilityScore: $suitabilityScore
      organizationId: $organizationId
      themeId: $themeId
    }) {
      financialProduct {
        ${SUGGESTED_PRODUCT}
      }
    }
  }
`;

export const FETCH_MODEL_PORTFOLIOS = gql`
  query fetchModelPortfolios($input: FetchModelPortfoliosInput!) {
    fetchModelPortfolios(input: $input) {
      modelPortfolios {
      riskReturnLevel
      ${SUGGESTED_PRODUCT}
      }
    }
  }
`;

export const FETCH_THEMES = gql`
  query fetchThemes($input: FetchThemesInput!) {
    fetchThemes(input: $input) {
      themes {
        id
        key
        translatedName { ${TRANSLATED_STRING_FIELDS} }
        translatedDescription { ${TRANSLATED_STRING_FIELDS} }
        core
      }
    }
  }
`;

export const buildAssignPortfolioFetchThemesInput = (
  organizationId: string | undefined,
) => ({
  filter: {
    organizationId,
    state: ThemeState.Active,
  },
  pagination: { perPage: 1000 },
});

/**
 * Filters subaccounts to exclude inactive ones and match the current account
 * @param subAccounts - Array of subaccounts to filter
 * @param currentAccountId - ID of the current account to match
 * @returns Array of non-inactive subaccounts for the current account
 */
export const filterActiveSubAccounts = (
  subAccounts: SubAccount[],
  currentAccountId: string,
): SubAccount[] =>
  subAccounts.filter(
    (subAccount: SubAccount) =>
      subAccount.state !== 'INACTIVE' &&
      subAccount.account.id === currentAccountId,
  );

/**
 * Determines available themes based on configuration and existing themes
 * @param config - Configuration object containing theme assignment settings
 * @param existingThemes - Existing themes available to the entity
 * @returns Array of available themes
 */
export const determineAvailableThemes = (
  config: Pick<
    PortfolioDataTransformConfig,
    'manuallyAssignModelPortfolios' | 'allThemesData'
  >,
  existingThemes: Theme[] | null = [],
): Theme[] => {
  if (config.manuallyAssignModelPortfolios) {
    return config.allThemesData?.fetchThemes?.themes || existingThemes || [];
  }
  return existingThemes || [];
};

/**
 * Finds the default (core) theme from available themes
 * @param availableThemes - Array of available themes
 * @returns The core theme if found, undefined otherwise
 */
export const findDefaultTheme = (availableThemes: Theme[]): Theme | undefined =>
  availableThemes.find((theme: Theme) => theme.core === true);

export const shouldFetchModelPortfoliosForTheme = ({
  manuallyAssignModelPortfolios,
  themeId,
  modelPortfoliosThemeId,
  modelPortfolioLoadFailed,
  modelPortfolioLoadInFlight,
}: {
  manuallyAssignModelPortfolios: boolean;
  themeId: string | null | undefined;
  modelPortfoliosThemeId: string | null | undefined;
  modelPortfolioLoadFailed?: boolean;
  modelPortfolioLoadInFlight?: boolean;
}) =>
  !!manuallyAssignModelPortfolios &&
  !!themeId &&
  modelPortfoliosThemeId !== themeId &&
  !modelPortfolioLoadFailed &&
  !modelPortfolioLoadInFlight;

export const modelPortfoliosFromFetchResult = ({
  data,
  error,
}: {
  data?: {
    fetchModelPortfolios?: {
      modelPortfolios?: FinancialProduct[] | null;
    } | null;
  } | null;
  error?: unknown;
}) => {
  if (error) return;
  return data?.fetchModelPortfolios?.modelPortfolios ?? [];
};

export const applySuggestedPortfolio = ({
  subAccounts,
  index,
  requestedThemeId,
  suggestedProduct,
  manuallyAssignModelPortfolios,
}: {
  subAccounts: SubAccountPortfolioData[];
  index: number;
  requestedThemeId: string;
  suggestedProduct: FinancialProduct;
  manuallyAssignModelPortfolios: boolean;
}) => {
  const current = subAccounts[index];
  if (!current || current.themeId !== requestedThemeId) return subAccounts;

  const suggestionMatchesTheme =
    suggestedProduct.theme?.id === requestedThemeId;
  return subAccounts.map((data, i) =>
    i === index
      ? {
          ...data,
          suggestedFinancialProduct: suggestedProduct,
          suggestionMissed: !suggestionMatchesTheme,
          financialProduct:
            suggestionMatchesTheme &&
            (!manuallyAssignModelPortfolios || !data.financialProduct)
              ? suggestedProduct
              : data.financialProduct,
        }
      : data,
  );
};

/**
 * Transforms multi-subaccount data into portfolio data array
 * @param subAccounts - Array of subaccounts from goal data
 * @param config - Configuration for the transformation
 * @returns Array of SubAccountPortfolioData for multi-subaccount mode
 */
export const transformMultiSubAccountPortfolioData = (
  subAccounts: SubAccount[],
  config: PortfolioDataTransformConfig,
): SubAccountPortfolioData[] => {
  // Filter out inactive subaccounts for the current account
  const activeSubAccounts = filterActiveSubAccounts(
    subAccounts,
    config.currentAccountId,
  );

  return activeSubAccounts.map((subAccount: SubAccount) => {
    const availableThemes = determineAvailableThemes(
      config,
      subAccount.availableThemes,
    );
    const defaultTheme = findDefaultTheme(availableThemes);
    const existingThemeId = subAccount.financialProduct?.theme?.id;

    return {
      id: subAccount.id,
      name: subAccount.name ?? '',
      themeId: (existingThemeId || defaultTheme?.id) ?? null,
      availableThemes,
      suitabilityScore: subAccount.suitabilityScore,
      custodianSuitabilityScore: config.custodianSuitabilityScore,
      userSuitability: config.userSuitability,
      suggestedFinancialProduct: subAccount.suggestedFinancialProduct ?? null,
      financialProduct:
        subAccount.financialProduct ??
        subAccount.suggestedFinancialProduct ??
        null,
      availableFinancialProducts: [],
      modelPortfoliosThemeId: null,
      modelPortfolioLoadFailed: false,
      modelPortfolioLoadFailureVersion: 0,
      suggestionMissed: false,
    };
  });
};

/**
 * Transforms single subaccount data into portfolio data array
 * @param subAccount - Single subaccount data
 * @param config - Configuration for the transformation
 * @returns Array containing single SubAccountPortfolioData
 */
export const transformSingleSubAccountPortfolioData = (
  subAccount: SubAccount,
  config: PortfolioDataTransformConfig,
): SubAccountPortfolioData[] => {
  const availableThemes = determineAvailableThemes(
    config,
    subAccount.availableThemes ?? [],
  );
  const defaultTheme = findDefaultTheme(availableThemes);
  const existingThemeId = subAccount.financialProduct?.theme?.id;

  return [
    {
      id: subAccount.id,
      name: subAccount.name ?? '',
      themeId: (existingThemeId || defaultTheme?.id) ?? null,
      availableThemes,
      suitabilityScore: subAccount.suitabilityScore,
      custodianSuitabilityScore: config.custodianSuitabilityScore,
      userSuitability: config.userSuitability,
      suggestedFinancialProduct: subAccount.suggestedFinancialProduct ?? null,
      financialProduct:
        subAccount.financialProduct ??
        subAccount.suggestedFinancialProduct ??
        null,
      availableFinancialProducts: [],
      modelPortfoliosThemeId: null,
      modelPortfolioLoadFailed: false,
      modelPortfolioLoadFailureVersion: 0,
      suggestionMissed: false,
    },
  ];
};

/**
 * Transforms goal data into portfolio data array for goal-based portfolio assignment
 * @param goal - Goal data object
 * @param config - Configuration for the transformation
 * @param currentGoalId - ID of the current goal
 * @returns Array containing single SubAccountPortfolioData for the goal, or empty array for fallback
 */
export const transformGoalPortfolioData = (
  goal: Goal,
  config: PortfolioDataTransformConfig,
  currentGoalId: string,
): SubAccountPortfolioData[] => {
  const availableThemes = determineAvailableThemes(
    config,
    goal.availableThemes,
  );
  const defaultTheme = findDefaultTheme(availableThemes);
  const existingThemeId = goal.financialProduct?.theme?.id;

  // Special case: hardcoded workflow with goal that has no themeId
  // Return empty array to indicate fallback to subAccount data
  if (
    config.isCreateSubAccountForAccountHardcodedWorkflow &&
    !existingThemeId
  ) {
    return [];
  }

  return [
    {
      id: currentGoalId || '',
      name: 'Goal',
      themeId: (existingThemeId || defaultTheme?.id) ?? null,
      availableThemes,
      suitabilityScore: goal.suitabilityScore,
      custodianSuitabilityScore: config.custodianSuitabilityScore,
      userSuitability: config.userSuitability,
      suggestedFinancialProduct: goal.suggestedFinancialProduct ?? null,
      financialProduct:
        goal.financialProduct ?? goal.suggestedFinancialProduct ?? null,
      availableFinancialProducts: [],
      modelPortfoliosThemeId: null,
      modelPortfolioLoadFailed: false,
      modelPortfolioLoadFailureVersion: 0,
      suggestionMissed: false,
      isFromGoal: !!config.isCreateSubAccountForAccountHardcodedWorkflow, // Only true for hardcoded workflow with themeId
    },
  ];
};

/**
 * Determines user suitability based on object type and data
 * @param objectType - Type of object ('HOUSEHOLD' or other)
 * @param userSuitabilityData - Data containing user suitability information
 * @returns User suitability score or null
 */
export const selectSuitabilitySourceData = (
  objectType: string,
  householdData: unknown,
  userSuitabilityData: unknown,
) =>
  objectType === PageObjectType.HOUSEHOLD ? householdData : userSuitabilityData;

export const shouldSkipFetchUserSuitability = (
  objectType: string,
  shouldSkipPortfolioAssignmentQueries: boolean,
) =>
  objectType === PageObjectType.HOUSEHOLD ||
  shouldSkipPortfolioAssignmentQueries;

export const determineUserSuitability = (
  objectType: string,
  userSuitabilityData: any,
): number | null => {
  if (objectType === PageObjectType.HOUSEHOLD) {
    return (
      userSuitabilityData?.fetchClientGroup?.clientGroup?.primaryUser
        ?.suitabilityScore || null
    );
  }
  return userSuitabilityData?.fetchUser?.user?.suitabilityScore || null;
};

/**
 * Determines user suitability based on object type and data
 * @param subAccountData - Subaccount data
 * @param useExternalSuitabilityScore - Whether to use external suitability score
 * @param options - Options object
 * @returns Suitability score for the subAccount depending on the option configuration
 */
export const suitabilityScore = (
  subAccountData: SubAccountPortfolioData,
  useExternalSuitabilityScore: boolean,
  options: any,
) => {
  if (useExternalSuitabilityScore) {
    return subAccountData.custodianSuitabilityScore;
  }

  if (options.enableMultiSubAccountMode) {
    return subAccountData.suitabilityScore;
  }

  return min([subAccountData.suitabilityScore, subAccountData.userSuitability]);
};
